import { ChangeDetectionStrategy, Component, effect, inject, input, OnInit, output, signal } from '@angular/core';
import { NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CardItem } from '@interface/card.interface';
import { MarkdownModule } from 'ngx-markdown';
import { AssetsApiService } from 'src/app/core/http/services/assets.service';
import { ConfirmationPopupComponent } from 'src/app/shared/common-ui-components/confirmation-popup/confirmation-popup.component';
import { LoaderComponent } from 'src/app/shared/common-ui-components/loader/loader.component';
import { CopilotModalComponent } from 'src/app/shared/components/copilot-modal/copilot-modal.component';
import { CopilotStorageService } from 'src/app/shared/services/copilot-storage.service';
import { UIIconsComponent } from 'src/app/shared/ui/icons/icons.component';
import { cloneDeep } from 'lodash';

interface ActionItem {
  name: string;
  strokeWidth: string;
  clickAction: string;
  label: string;
  disabled?: boolean;
}

@Component({
  selector: 'app-canvas-actions',
  standalone: true,
  imports: [
    NgClass,
    UIIconsComponent,
    FormsModule,
    LoaderComponent,
    CopilotModalComponent,
    MarkdownModule,
    ConfirmationPopupComponent
  ],
  templateUrl: './canvas-actions.component.html',
  styleUrl: './canvas-actions.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CanvasActionsComponent implements OnInit {
  // Inputs (developer preview)
  canvasId = input<string>('');
  cards = input<CardItem[]>([]);
  selectedReportIds = input<string[]>([]);
  selectedAnswerIds = input<string[]>([]);
  freeFormCards = input<{ cardId: string, assetId: string, content: string }[]>([]);
  unsavedFreeFormCards = input<{ cardId: string, hasUnsavedChanges: boolean }[]>([]);
  getLatestCards = input<() => CardItem[]>(() => []);

  // These inputs are also used for local editing — keep local writable signals synced via effect
  canvasNameInput = input<string>('', { alias: 'canvasName' });
  canvasDescriptionInput = input<string>('', { alias: 'canvasDescription' });
  conversationsInput = input<{ questions: any[], answers: any[] }>({ questions: [], answers: [] }, { alias: 'conversations' });

  // Local editable signals (two-way via ngModel)
  canvasName = signal<string>('');
  canvasDescription = signal<string>('');
  localConversations = signal<{ questions: any[], answers: any[] }>({ questions: [], answers: [] });

  // Outputs (developer preview)
  refreshCanvas = output<void>();
  actionToggled = output<boolean>();
  afterSaveCanvas = output<any>();
  toggleLockCanvas = output<boolean>();
  askQuestion = output<string>();
  emitConversations = output<{ questions: any[], answers: any[] }>();

  // State signals
  selectedAction = signal<ActionItem | null>(null);
  isQuickModalOpen = signal<boolean>(false);
  actionName = signal<string>('');
  isCanvasLocked = signal<boolean>(false);
  isCanvasNameEmpty = signal<boolean>(false);
  saveCanvasLoading = signal<boolean>(false);
  saveCanvasSuccessful = signal(false);
  saveCanvasError = signal(false);
  tags = signal<string[]>([]);
  tagInput = signal<string>('');
  redirectTime: number = 2;
  intervalRedirectTime: any;
  redirecting = signal<boolean>(false);
  loadingAction = signal(false);
  newCanvasId: string = '';
  showConfirmationPopup = signal<boolean>(false);
  confirmationPopupMsg = signal<string>('');
  confirmationPopupHeader = signal<string>('');
  confirmationCallbacks: any = {
    confirmAction: () => this.closeConfirmationPopUp(),
    cancelAction: () => this.closeConfirmationPopUp()
  };

  public quickActions: ActionItem[] = [];
  public assetModelId: string = '';

  private assetsApiService = inject(AssetsApiService);
  private router = inject(Router);
  private copilotStorageService = inject(CopilotStorageService);

  constructor() {
    // Sync inputs to local signals
    effect(() => {
      const name = this.canvasNameInput();
      if (name && !this.canvasName()) {
        this.canvasName.set(name);
      }
    }, { allowSignalWrites: true });
    effect(() => {
      const desc = this.canvasDescriptionInput();
      if (desc && !this.canvasDescription()) {
        this.canvasDescription.set(desc);
      }
    }, { allowSignalWrites: true });
    effect(() => {
      this.localConversations.set(this.conversationsInput());
    }, { allowSignalWrites: true });
  }

  ngOnInit(): void {
    this.initializeActions();
  }

  private initializeActions(): void {
    this.quickActions = [
      {
        name: 'reload',
        strokeWidth: '1.5px',
        clickAction: 'REFRESH',
        label: 'Refresh Canvas',
        disabled: !this.canvasId()
      },
      {
        name: 'lock-open',
        strokeWidth: '1.5px',
        clickAction: 'LOCK',
        label: 'Lock Canvas',
      },
      {
        name: 'gear',
        strokeWidth: '2px',
        clickAction: 'SETTING',
        label: 'Canvas Settings',
        disabled: true
      },
      {
        name: 'lightbulb',
        strokeWidth: '2px',
        clickAction: 'INSIGHTS',
        label: 'AI Generated Insights',
        disabled: true
      },
      {
        name: 'data-copilot',
        strokeWidth: '2px',
        clickAction: 'COPILOT',
        label: 'Speak with Copilot',
        disabled: false
      },
      {
        name: 'save',
        strokeWidth: '2px',
        clickAction: 'SAVE',
        label: 'Save Canvas',
      }
    ];
  }

  onQuickActionClick(action: ActionItem): void {
    if (action.clickAction === 'REFRESH') {
      if (!action.disabled) {
        this.showConfirmation(
          'You will lose any newly added cards and unsaved changes. Are you sure you want to refresh the canvas?',
          'Refresh Canvas',
          () => this.refreshCanvas.emit()
        );
      }
      return;
    }

    if (action.clickAction === 'LOCK') {
      this.isCanvasLocked.update(locked => !locked);
      this.toggleLockCanvas.emit(this.isCanvasLocked());

      const lockActionIndex = this.quickActions.findIndex(a => a.clickAction === 'LOCK');
      if (lockActionIndex !== -1) {
        this.quickActions[lockActionIndex].name = this.isCanvasLocked() ? 'lock-closed' : 'lock-open';
      }
      return;
    }

    if (this.actionName() === action.clickAction) {
      this.closeQuickActionModal();
      return;
    }

    this.selectedAction.set(action);
    this.isQuickModalOpen.set(true);
    this.actionName.set(action.clickAction);
    this.actionToggled.emit(true);
  }

  closeQuickActionModal(): void {
    this.selectedAction.set(null);
    this.isQuickModalOpen.set(false);
    this.actionName.set('');
    this.actionToggled.emit(false);
  }

  getIconClasses(action: ActionItem): any {
    return {
      'stroke-[#344054]': !(action.clickAction === 'LOCK' && this.isCanvasLocked()),
      'hover:stroke-[#0F49A0]': !action.disabled && !(action.clickAction === 'LOCK' && this.isCanvasLocked()),
      'stroke-white': this.actionName() === action.clickAction,
      'stroke-[#0F49A0]': action.clickAction === 'LOCK' && this.isCanvasLocked(),
      [`stroke-[${action.strokeWidth}]`]: true,
    };
  }

  public toggleSaveCanvasSuccessful(): void {
    this.saveCanvasSuccessful.update(v => !v);
  }

  public toggleSaveCanvasError(): void {
    this.saveCanvasError.update(v => !v);
  }

  public onSaveCanvas(shouldReload: boolean = true, bypassUnsavedCheck: boolean = false): void {
    if (!this.canvasName().trim()) {
      this.isCanvasNameEmpty.set(true);
      return;
    }

    if (!bypassUnsavedCheck) {
      const unsavedCards = this.unsavedFreeFormCards().filter(card => card.hasUnsavedChanges);
      if (unsavedCards.length > 0) {
        this.showConfirmation(
          `There are ${unsavedCards.length} unsaved free-form card(s) in the canvas. Please save them first before saving the canvas.`,
          'Unsaved Free-Form Cards',
          () => {}
        );
        return;
      }
    }

    this.isCanvasNameEmpty.set(false);
    this.saveCanvasLoading.set(true);
    const canvasData = this.getCanvasDetails();

    if (this.canvasId()) {
      this.assetsApiService.updateAsset(canvasData).subscribe({
        next: (response: any) => {
          this.saveCanvasLoading.set(false);
          this.saveCanvasSuccessful.set(true);
          if (shouldReload) {
            this.afterSaveCanvas.emit(response);
          }
        },
        error: (err: Error) => {
          console.error('Error updating canvas:', err);
          this.saveCanvasLoading.set(false);
          this.saveCanvasError.set(true);
        },
        complete: () => {
          this.saveCanvasLoading.set(false);
        }
      });
    } else {
      this.assetsApiService.createAsset(canvasData).subscribe({
        next: (response: any) => {
          this.saveCanvasLoading.set(false);
          this.saveCanvasSuccessful.set(true);
          this.newCanvasId = response?.data?.createAsset?.id;
          this.startRedirectCountdown();
        },
        error: (err: Error) => {
          console.error('Error creating canvas:', err);
          this.saveCanvasLoading.set(false);
          this.saveCanvasError.set(true);
        },
        complete: () => {
          this.saveCanvasLoading.set(false);
        }
      });
    }
  }

  public addTag(): void {
    const tag = this.tagInput().trim();
    if (tag) {
      this.tags.update(tags => {
        if (!tags.includes(tag)) {
          return [...tags, tag];
        }
        return tags;
      });
      this.tagInput.set('');
    }
  }

  public removeTag(tag: string): void {
    this.tags.update(tags => tags.filter(t => t !== tag));
  }

  public onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === 'Tab' || event.key === ',' || event.key === ';') {
      event.preventDefault();
      this.addTag();
    }
  }

  private startRedirectCountdown(): void {
    this.loadingAction.set(true);
    this.redirecting.set(true);

    if (this.intervalRedirectTime) {
      clearInterval(this.intervalRedirectTime);
    }

    this.intervalRedirectTime = setInterval(() => {
      if (this.redirectTime > 1) {
        this.redirectTime--;
      } else {
        this.loadingAction.set(false);
        if (this.newCanvasId !== '') {
          this.router.navigate(['/dashboard/canvas', this.newCanvasId]);
        }
        clearInterval(this.intervalRedirectTime);
        this.redirectTime = 2;
        this.redirecting.set(false);
      }
    }, 1000);
  }

  private getCanvasDetails(): any {
    const latestCards = this.getLatestCards() ? this.getLatestCards()() : this.cards();
    const freeFormCardsMap = new Map(this.freeFormCards().map(card => [card.cardId, card]));

    return {
      ...(this.canvasId() ? { id: this.canvasId() } : {}),
      name: this.canvasName(),
      type: 'CANVAS',
      status: 'OPEN',
      description: this.canvasDescription(),
      tags: this.tags(),
      parameters: {
        isLocked: this.isCanvasLocked(),
        cards: latestCards.map(card => {
          const cardData: any = {
            id: card.id,
            x: card.x,
            y: card.y,
            cols: card.cols,
            rows: card.rows,
            title: card.title,
            reportId: card.reportId || null,
            questionId: card.questionId || null,
            answerId: card.answerId || null,
            conversationId: card.conversationId || null,
            questionText: card.questionText || null,
            cardType: card.cardType,
            data: card.data ? cloneDeep(card.data) : null,
            selectedVisualType: card.selectedVisualType || null,
            chartConfigurations: card.chartConfigurations ? cloneDeep(card.chartConfigurations) : null,
            gridConfigurations: card.gridConfigurations ? cloneDeep(card.gridConfigurations) : null
          };

          if (card.cardType === 'FREEFORM_CARD') {
            const freeFormData = freeFormCardsMap.get(card.id);
            if (freeFormData) {
              cardData.assetId = freeFormData.assetId;
            }
          }

          return cardData;
        })
      }
    };
  }

  public onConversations(payload: { questions: any[], answers: any[] }): void {
    this.localConversations.set(payload);
    this.emitConversations.emit(payload);

    if (this.canvasId()) {
      this.copilotStorageService.saveConversations(this.canvasId(), {
        questions: payload.questions,
        answers: payload.answers
      });
    }
  }

  public onAskQuestion(question: string): void {
    this.askQuestion.emit(question);
  }

  showConfirmation(message: string, header: string, confirmAction?: () => void): void {
    this.confirmationPopupMsg.set(message);
    this.confirmationPopupHeader.set(header || 'Confirmation');
    this.confirmationCallbacks.confirmAction = confirmAction
      ? () => { confirmAction(); this.closeConfirmationPopUp(); }
      : () => this.closeConfirmationPopUp();
    this.showConfirmationPopup.set(true);
  }

  closeConfirmationPopUp(): void {
    this.showConfirmationPopup.set(false);
    this.confirmationPopupMsg.set('');
    this.confirmationPopupHeader.set('');
  }
}
